/**
 * Post-auth return URL for this browser origin (`/auth/callback`).
 * Used for OAuth, **email magic links**, **password recovery**, and should match **admin invite** `redirectTo`
 * (see `api/admin/action.ts`) so PKCE `?code=` exchange happens on a single route.
 *
 * OAuth: passed to `signInWithOAuth({ options.redirectTo })`.
 *
 * ## Vercel preview / branch URLs vs production
 * Each deployment host (`*.vercel.app` or your custom domain) is a **separate origin**. Supabase keeps the
 * session in storage **per origin**. If Google sign-in finishes on **production**, preview never gets that
 * session — you are not “logged in” on the preview tab even if the address bar shows a preview URL later.
 *
 * If clicking “Sign in with Google” on a **preview** URL ends on **production**, Supabase is not allowing
 * `redirectTo` for that host. Fix in **Supabase Dashboard → Authentication → URL Configuration**:
 * - Keep **Site URL** as your production app URL.
 * - Under **Redirect URLs**, add patterns that match your previews, for example (adjust team slug as needed):
 *   - `http://localhost:5173/**` (local Vite)
 *   - `https://**--<vercel-team-slug>.vercel.app/**` (preview deployments; see Supabase redirect glob docs)
 *   - Or add the exact URL once: `https://<deployment-host>/auth/callback`
 *
 * @see https://supabase.com/docs/guides/auth/redirect-urls
 */
export function getOAuthRedirectTo(): string {
  if (typeof window === "undefined") return "";
  return `${window.location.origin}/auth/callback`;
}

/** Same as {@link getOAuthRedirectTo}; use for email OTP / recovery redirects in naming clarity. */
export function getEmailAuthRedirectTo(): string {
  return getOAuthRedirectTo();
}
